import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { buscaDaUrl } from '../../componentes/filtros-visao';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Clock, Layers, PencilLine, Plus, Search } from 'lucide-react';
import type {} from '@malgoos/shared';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Estado, Vazio } from '../../componentes/estados';
import { Entrada, Selecao } from '../../componentes/formulario';
import { getTodos } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { BASE, BarraProgresso, Capa } from './comum';
import { EditorCurso } from './EditorCurso';
import { duracaoTexto, type Curso } from './tipos';

/** M11.7: catálogo de cursos. Clicar abre a área de membros; quem gerencia também vê rascunhos e o editor. */
export function Cursos() {
  const { t } = useTranslation('treinamento');
  const s = useSessao();
  const navegar = useNavigate();
  const [busca, setBusca] = useSearchParams();
  // Review r24 spec (B4): o link da novidade (curso ainda não publicado) chega com `?busca=<título>`: a busca nasce dele.
  const [texto, setTexto] = useState(() => buscaDaUrl(busca));
  const [situacao, setSituacao] = useState('todos');
  const [publicacao, setPublicacao] = useState('');
  const podeEditar = s.pode('treinamento.editar');
  // Val5 (29/10): criar curso pede `treinamento.criar` (antes `treinamento.editar`).
  const podeCriar = s.pode('treinamento.criar');
  const editar = busca.get('editar');

  const q = useQuery({
    queryKey: ['treinamento', 'cursos', texto, situacao, publicacao],
    queryFn: () => getTodos<Curso>('/treinamento/cursos', { busca: texto || undefined, situacao, publicado: podeEditar ? publicacao || undefined : undefined }),
  });

  const abrirEditor = (id: string) => setBusca({ editar: id });
  const fecharEditor = () => setBusca({}, { replace: true });

  return (
    <>
      {(podeCriar || s.pode('treinamento.excluir')) && (
        <AcaoPrimaria>
          {s.pode('treinamento.excluir') && <BotaoLixeira entidades={['treinamento.curso']} />}
          {podeCriar && (
            <>
              <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => abrirEditor('novo')}>
                {t('acoes.novoCurso')}
              </Botao>
            </>
          )}
        </AcaoPrimaria>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 basis-56">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
          <Entrada aria-label={t('acoes.buscar')} placeholder={t('acoes.buscar')} className="pl-9" value={texto} onChange={(e) => setTexto(e.target.value)} />
        </div>
        <div className="w-full sm:w-56">
          <Selecao
            aria-label={t('filtros.situacao')}
            value={situacao}
            onChange={(e) => setSituacao(e.target.value)}
            opcoes={[
              { valor: 'todos', rotulo: t('filtros.todos') },
              { valor: 'minhas', rotulo: t('filtros.minhas') },
              { valor: 'em_andamento', rotulo: t('filtros.emAndamento') },
              { valor: 'concluidos', rotulo: t('filtros.concluidos') },
              { valor: 'sem_matricula', rotulo: t('filtros.semMatricula') },
            ]}
          />
        </div>
        {podeEditar && (
          <div className="w-full sm:w-56">
            <Selecao
              aria-label={t('filtros.publicacao')}
              value={publicacao}
              onChange={(e) => setPublicacao(e.target.value)}
              opcoes={[
                { valor: '', rotulo: t('filtros.todosCursos') },
                { valor: 'true', rotulo: t('filtros.publicados') },
                { valor: 'false', rotulo: t('filtros.rascunhos') },
              ]}
            />
          </div>
        )}
      </div>

      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <Vazio texto={podeEditar && !texto && situacao === 'todos' && !publicacao ? t('vazio.cursosGestor') : t('vazio.cursos')} />
          ) : (
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {d.itens.map((c) => (
              <li key={c.id} className="min-w-0">
                <article className="flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-borda bg-sup-1">
                  <button type="button" onClick={() => navegar(`${BASE}/curso/${c.id}`)} className={`block w-full overflow-hidden border-b border-borda text-left ${c.capa_anexo_id ? 'aspect-video' : 'h-24'}`} aria-label={c.titulo}>
                    <Capa anexoId={c.capa_anexo_id} titulo={c.titulo} />
                  </button>
                  <div className="flex min-w-0 flex-1 flex-col gap-2 p-4">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {!c.publicado && <Selo cor="laranja">{t('selos.rascunho')}</Selo>}
                      {c.bloqueado && <Selo cor="perigo">{t('selos.bloqueado')}</Selo>}
                      {c.minha?.concluida_em ? <Selo cor="verde">{t('selos.concluido')}</Selo> : c.minha && <Selo cor="azul">{t('selos.emAndamento')}</Selo>}
                      {c.minha?.origem === 'trilha' && <Selo>{t('selos.trilha')}</Selo>}
                    </div>
                    <h2 className="font-semibold">
                      <button type="button" onClick={() => navegar(`${BASE}/curso/${c.id}`)} className="text-left hover:text-acento-forte">
                        {c.titulo}
                      </button>
                    </h2>
                    {c.descricao && <p className="line-clamp-2 text-sm text-tinta-3" title={c.descricao}>{c.descricao}</p>}
                    <p className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-1 text-xs text-tinta-3">
                      <span className="inline-flex items-center gap-1">
                        <Layers className="size-3.5" aria-hidden />
                        <span className="numero">{t('catalogo.aulas', { count: c.aulas })}</span>
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="size-3.5" aria-hidden />
                        <span className="numero">{duracaoTexto(c.carga_horaria_min || c.duracao_min)}</span>
                      </span>
                    </p>
                    {c.pre_requisito_titulo && <p className="text-xs text-tinta-3">{t('catalogo.exige', { titulo: c.pre_requisito_titulo })}</p>}
                    {c.minha && <BarraProgresso valor={c.minha.percentual} />}
                    {podeEditar && (
                      <div className="flex items-center justify-between gap-2 border-t border-borda pt-3">
                        <span className="numero text-xs text-tinta-3">{t('catalogo.matriculados', { count: c.matriculados })}</span>
                        <Botao tamanho="sm" icone={<PencilLine className="size-4" />} onClick={() => abrirEditor(c.id)}>
                          {t('acoes.editar')}
                        </Botao>
                      </div>
                    )}
                  </div>
                </article>
              </li>
            ))}
          </ul>
          )
        }
      </Estado>

      {(editar === 'novo' ? podeCriar : podeEditar) && editar && <EditorCurso cursoId={editar === 'novo' ? null : editar} aoFechar={fecharEditor} aoCriar={abrirEditor} />}
    </>
  );
}
